import { useQuery } from "@tanstack/react-query";
import type React from "react";

import { Button } from "@/components/Button/Button";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { type RatableItem, RatableSuggestionsList } from "@/components/RatableSuggestionsList/RatableSuggestionsList";
import { surveyMutations, useSurveyQueries } from "@/queries/surveys";
import { useTranslation } from "@/translations";

type SurveyFollowUpModalProps = ModalBaseProps & {
  surveyId: string;
};

export function SurveyFollowUpTipsModal({
  surveyId,
  isOpened,
  onOpenChange,
}: SurveyFollowUpModalProps): React.ReactNode {
  const { t } = useTranslation();

  const queries = useSurveyQueries();
  const {
    data: surveyFollowUps,
    isPending,
    error,
  } = useQuery({
    ...queries.suggestedFollowUps(surveyId),
    enabled: isOpened,
  });

  const rateMutation = surveyMutations.useRateSurveyFollowUp();
  const handleRateFollowUp = (followUpId: string, ratingValue: "positive" | "negative") => {
    rateMutation.mutate({
      surveyId,
      followUpId,
      rating: ratingValue,
    });
  };

  const ratableItems: RatableItem[] =
    surveyFollowUps?.map((suggestion) => ({
      id: suggestion.id,
      title: suggestion.suggestion,
      details: [suggestion.rationale],
      rating: suggestion.rating,
      ratedAt: suggestion.ratedAt,
    })) || [];

  return (
    <Modal.Root
      size="md"
      title={t("page.survey-details.follow-up-modal.title")}
      description={t("page.survey-details.follow-up-modal.description")}
      {...{ isOpened, onOpenChange }}
    >
      {isPending && <FullSizeLoader withPadding />}

      {error && (
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <p className="text-body text-red-600">{t("page.survey-details.follow-up-modal.error")}</p>
        </div>
      )}

      {surveyFollowUps && ratableItems.length > 0 && !isPending && !error && (
        <div className="flex w-full flex-col gap-2">
          <h4 className="text-headline4">{t("page.survey-details.follow-up-modal.suggestions-title")}</h4>
          <RatableSuggestionsList items={ratableItems} onRateItem={handleRateFollowUp} />
        </div>
      )}
      <Modal.Actions>
        <Modal.Close>
          <Button>{t("common.action.close")}</Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
